import React from 'react';
import { useTranslation } from 'react-i18next';
import { Site } from 'graphql-common';
import _get from 'lodash/get';
import { Values } from '@lib/interfaces/form';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ConductCodeView from 'components/ConductCodeView/ConductCodeView';
import ConductCodeForm from 'components/ConductCodeForm/ConductCodeForm';
import { materialSymbolsOutlined } from '@lib/enums/common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Typography from '@lib/components/Typography/Typography';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { IconName } from '@lib/components/Modal/enums';
import { UseSiteActionsResult } from 'routes/Sites/utils/useSiteActions';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import { SiteViewMode } from 'routes/Sites/enums';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import styles from './Site.module.scss';

interface Props {
  useSiteActionsResult: UseSiteActionsResult;
  useModalManagementResult: UseModalManagementResult;
  viewMode: SiteViewMode;
}

function SiteCodeOfConduct(props: Props) {
  const { useSiteActionsResult, useModalManagementResult, viewMode } = props;
  const {
    onConfirmedDeleteCodeOfConduct,
    onEdit,
    onEditCodeOfConduct,
    onViewCodeOfConduct,
    siteQuery,
    updateMutationResult,
  } = useSiteActionsResult;
  const codeOfConductDeleteLoading = updateMutationResult.loading;
  const { openModal, closeModal, isModalOpened } = useModalManagementResult;
  const openSiteCodeOfConductDeleteModal = () =>
    openModal(Modals.SiteCodeOfConductDestroyModal);
  const closeSiteCodeOfConductDeleteModal = () =>
    closeModal(Modals.SiteCodeOfConductDestroyModal);
  const { t } = useTranslation();
  const siteData = _get(siteQuery, 'data.site') as Site;

  const getTitle = () => {
    if (viewMode === SiteViewMode.ViewCodeOfConduct)
      return t('view-code-of-conduct');
    if (viewMode === SiteViewMode.EditCodeOfConduct)
      return t('edit-code-of-conduct');
    return t('code-of-conduct');
  };

  if (siteQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteQuery.error)}
        description={getDescriptionFromError(siteQuery.error)}
      />
    );
  }

  return (
    <DashboardContentMaxWidth>
      <PageHeader title={getTitle()} />
      <PageBody>
        {siteQuery.loading ? (
          <CircleLoader />
        ) : (
          <>
            <WhiteBox>
              {viewMode === SiteViewMode.ViewCodeOfConduct && (
                <div className={styles.header}>
                  <Typography variant="h2" className={styles.title}>
                    {siteData?.name}
                  </Typography>
                  <Button
                    buttonText={t('edit')}
                    buttonType={ButtonTypes.primaryFilled}
                    leftIcon="edit"
                    onClick={onEditCodeOfConduct}
                  />
                </div>
              )}
              {viewMode === SiteViewMode.ViewCodeOfConduct && (
                <ConductCodeView siteData={siteData} />
              )}
              {viewMode === SiteViewMode.EditCodeOfConduct && (
                <ConductCodeForm
                  siteData={siteData as Values}
                  loading={updateMutationResult.loading}
                  onCancel={onViewCodeOfConduct}
                  onSubmit={onEdit}
                  graphQLErrors={getGraphQLErrors(updateMutationResult.error)}
                />
              )}
            </WhiteBox>
            {siteData.siteConductCodeSections?.length > 0 && (
              <div className={styles.conductCodeSectionsFooter}>
                <Button
                  buttonText={t('delete-code-of-conduct')}
                  buttonType={ButtonTypes.negativeOutlined}
                  buttonSize={ButtonSizes.small}
                  leftIcon="delete"
                  leftIconClassName={materialSymbolsOutlined}
                  onClick={openSiteCodeOfConductDeleteModal}
                />
              </div>
            )}
          </>
        )}
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={t('delete-code-of-conduct-text')}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SiteCodeOfConductDestroyModal)}
        loading={codeOfConductDeleteLoading}
        onClose={closeSiteCodeOfConductDeleteModal}
        onConfirm={onConfirmedDeleteCodeOfConduct}
        title={t('are-you-sure')}
        useProgress
      />
    </DashboardContentMaxWidth>
  );
}

export default SiteCodeOfConduct;
